import { DocumentRenderData, IdentifiedPropertyForDoc, RequiredField } from '../types'
import { QISignatureBlock } from './shared'

export const identificationFormRequiredFields: RequiredField[] = [
  { key: 'identification.exchangerNames', description: 'Exchanger full name', sourceHub: 'identification', sourceStep: 'Identification Form' },
  { key: 'identification.identificationDeadline', description: 'Identification deadline date', sourceHub: 'identification', sourceStep: 'Identification Form' },
  { key: 'identification.identificationRule', description: 'Identification rule selection (3-property / 200% / 95%)', sourceHub: 'identification', sourceStep: 'Identification Form' },
  { key: 'identification.exchangeAgreementDate', description: 'Exchange Agreement date', sourceHub: 'identification', sourceStep: 'Identification Form' },
  { key: 'identification.relinquishedPropertySaleDate', description: 'Relinquished property sale date', sourceHub: 'sales', sourceStep: 'Relinquished Property' },
  { key: 'identification.identifiedProperties', description: 'At least one identified replacement property', sourceHub: 'identification', sourceStep: 'Identification Form', minArrayLength: 1 },
]

interface Props {
  data: DocumentRenderData
}

function formatUSD(value: number | undefined): string {
  if (value == null || !isFinite(value) || value <= 0) return ''
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function blank(value: string | undefined, width: number = 40): string {
  if (value && value.trim()) return value
  return '_'.repeat(width)
}

function PropertyBlock({
  index,
  property,
  showFmvAsInformationalOnly,
}: {
  index: number
  property: IdentifiedPropertyForDoc | null
  showFmvAsInformationalOnly: boolean
}) {
  const streetAndCommon = property
    ? property.commonName
      ? `${property.address} (${property.commonName})`
      : property.address
    : ''
  const legalLine = property
    ? property.legalDescription || property.taxParcelNumber
      ? [property.legalDescription, property.taxParcelNumber].filter(Boolean).join(' / ')
      : property.cityCountyState ?? ''
    : ''
  const cityCountyState = property?.cityCountyState ?? ''
  const fmvLabel = showFmvAsInformationalOnly
    ? 'Fair Market Value (for informational purposes only):'
    : 'Fair Market Value:'
  const fmv = formatUSD(property?.estimatedFMV)
  const hasPartialInterest =
    property != null &&
    ((property.percentageInterest != null && property.percentageInterest > 0 && property.percentageInterest < 100) ||
      (property.dollarValueAcquired != null && property.dollarValueAcquired > 0))
  const partialInterestLabel = property?.percentageInterest
    ? `${property.percentageInterest}%`
    : property?.dollarValueAcquired
    ? formatUSD(property.dollarValueAcquired)
    : ''

  return (
    <div className="border border-dashed border-border rounded-md p-3 space-y-2 bg-card/40">
      <p className="font-semibold text-sm">Property #{index + 1}.</p>
      <div className="space-y-1.5 text-xs">
        <p>
          <span className="font-medium">Street Address (and common name, if applicable):</span>
          <span className="block ml-2 mt-0.5 font-mono">{blank(streetAndCommon, 60)}</span>
        </p>
        <p className="leading-relaxed">
          If there is no street address for the property, provide a legal description or tax parcel
          number (including city, county, and state):
          <span className="block ml-2 mt-0.5 font-mono">
            {blank(legalLine || cityCountyState, 60)}
          </span>
        </p>
        <p>
          <span className="font-medium">{fmvLabel} $</span>
          <span className="font-mono">{blank(fmv.replace('$', ''), 30)}</span>
        </p>
        <p>If you are acquiring less than a 100% interest in the property:</p>
        <p className="ml-2">
          <span className="font-medium">Percentage Interest OR Dollar Value Being Acquired:</span>
          <span className="font-mono ml-2">{blank(hasPartialInterest ? partialInterestLabel : '', 30)}</span>
        </p>
      </div>
    </div>
  )
}

export function IdentificationFormTemplate({ data }: Props) {
  const { identification, signature } = data
  if (!identification) return null

  const rule = identification.identificationRule
  const properties = identification.identifiedProperties
  const totalIdentified = properties.reduce((sum, p) => sum + (p.estimatedFMV || 0), 0)

  const threePropertySelected = rule === '3-property'
  const twoHundredPercentSelected = rule === '200-percent'
  const ninetyFivePercentSelected = rule === '95-percent'

  const threePropertySlots: (IdentifiedPropertyForDoc | null)[] = threePropertySelected
    ? [properties[0] ?? null, properties[1] ?? null, properties[2] ?? null]
    : []
  const twoHundredSlots: (IdentifiedPropertyForDoc | null)[] = twoHundredPercentSelected || ninetyFivePercentSelected
    ? Array.from({ length: Math.max(4, properties.length) }, (_, i) => properties[i] ?? null)
    : []

  return (
    <div className="space-y-5 text-sm text-foreground/85 font-serif">
      <div className="text-center">
        <h4 className="text-xl font-bold tracking-wide text-foreground">45-DAY IDENTIFICATION NOTICE</h4>
        <p className="text-xs text-muted-foreground/80 mt-1 italic">
          Pursuant to Treas. Reg. §1.1031(k)-1(c)
        </p>
      </div>

      <div className="rounded-md border border-border bg-card/50 p-3 space-y-2 text-xs">
        <p>
          <span className="font-semibold">EXCHANGER NAME:</span>{' '}
          <span className="font-mono">{blank(identification.exchangerNames, 50)}</span>
        </p>
        <p>
          <span className="font-semibold">EXCHANGE AGREEMENT DATE:</span>{' '}
          <span className="font-mono">{blank(identification.exchangeAgreementDate, 30)}</span>
        </p>
        <p>
          <span className="font-semibold">RELINQUISHED PROPERTY SALE DATE:</span>{' '}
          <span className="font-mono">{blank(identification.relinquishedPropertySaleDate, 30)}</span>
        </p>
        {identification.open1031FileNumber && (
          <p>
            <span className="font-semibold">OPEN1031 FILE NO.:</span>{' '}
            <span className="font-mono">{identification.open1031FileNumber}</span>
          </p>
        )}
      </div>

      <p className="text-xs leading-relaxed">
        Please review this form carefully and complete sections 1, either 2 or 3, and 4. This form is
        due to Open1031 no later than midnight, Pacific Time, on the forty-fifth (45th) day after the
        Relinquished Property Sale Date set forth above (deadline:{' '}
        <span className="font-semibold">{blank(identification.identificationDeadline, 18)}</span>).
        Please contact us immediately if you intend to use the alternative identification procedures
        described in Section 3.3 of the Exchange Agreement or if you believe you qualify for a
        &ldquo;disaster relief&rdquo; extension as described in Section 1.2 of the Exchange Agreement.
        Capitalized terms used but not defined herein have the meanings given to them in the Exchange
        Agreement.
      </p>

      <p className="text-xs leading-relaxed italic">
        The undersigned hereby designates the properties described herein as Eligible Replacement
        Properties for the Exchange:
      </p>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">1. Selection of Property Identification Rule:</h5>
        <p className="text-[11px] italic text-muted-foreground">
          Check only ONE box and fill out either Section 2 or Section 3 below.
        </p>
        <div className="space-y-1 text-xs">
          <div className="flex items-start gap-2">
            <span className="font-mono text-base">{threePropertySelected ? '[x]' : '[ ]'}</span>
            <p>
              I am identifying under the <strong>THREE PROPERTY RULE</strong> (may identify up to three
              properties, regardless of their market value).
            </p>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-mono text-base">{twoHundredPercentSelected ? '[x]' : '[ ]'}</span>
            <p>
              I am identifying under the <strong>200% RULE</strong> (may identify any number of
              properties, but the total fair market value cannot be more than 200% of the value of the
              relinquished property).
            </p>
          </div>
          {ninetyFivePercentSelected && (
            <div className="flex items-start gap-2">
              <span className="font-mono text-base">[x]</span>
              <p>
                I am identifying under the <strong>95% RULE</strong> (may identify any number of
                properties of any value, but must close on at least 95% of the aggregate identified
                fair market value).
              </p>
            </div>
          )}
        </div>
      </section>

      {threePropertySelected && (
        <section className="space-y-3">
          <h5 className="font-semibold text-sm">
            2. If you are identifying under the THREE PROPERTY RULE, complete this section:
          </h5>
          {threePropertySlots.map((property, index) => (
            <PropertyBlock
              key={index}
              index={index}
              property={property}
              showFmvAsInformationalOnly
            />
          ))}
        </section>
      )}

      {(twoHundredPercentSelected || ninetyFivePercentSelected) && (
        <section className="space-y-3">
          <h5 className="font-semibold text-sm">
            3. If you are identifying under the {twoHundredPercentSelected ? '200% RULE' : '95% RULE'},
            complete this section:
          </h5>
          <div className="space-y-1.5 text-xs">
            <p>
              <span className="font-medium">The total value of the properties identified below is:</span>{' '}
              ${' '}
              <span className="font-mono">{blank(formatUSD(totalIdentified).replace('$', ''), 25)}</span>
            </p>
            <p>
              <span className="font-medium">The value of the Relinquished Property I sold is:</span>{' '}
              ${' '}
              <span className="font-mono">
                {blank(formatUSD(identification.relinquishedPropertyFMV).replace('$', ''), 25)}
              </span>
            </p>
            <p className="italic text-muted-foreground">
              Note: If a property identified below is not subject to a binding sale contract, provide
              your best estimate for the property&rsquo;s current fair market value.
            </p>
          </div>
          {twoHundredSlots.map((property, index) => (
            <PropertyBlock
              key={index}
              index={index}
              property={property}
              showFmvAsInformationalOnly={false}
            />
          ))}
          <p className="text-[11px] italic text-muted-foreground">
            [Attach additional sheets as necessary.]
          </p>
        </section>
      )}

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">4. Exchanger Acknowledgments.</h5>
        <p className="text-xs leading-relaxed">
          Exchanger acknowledges that it is solely responsible for the validity of the identification;
          the timely delivery of the identification; the accuracy and sufficiency of the descriptions
          of the Eligible Replacement Properties identified; the fair market values thereof (if
          applicable); and, if the Three Property Rule is used, whether any property identified is a
          single property.
        </p>
        <p className="text-xs leading-relaxed">
          All exchange funds are held by Open1031 Platform as Qualified Intermediary in a segregated
          qualified escrow account, preventing constructive receipt.
        </p>
      </section>

      <p className="text-[11px] italic uppercase tracking-wider">Signatures follow.</p>

      <section className="border-t-2 border-border pt-4 space-y-4">
        <div>
          <p className="font-semibold text-sm mb-2">Submitted by Exchanger:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <p>
              <span className="font-medium">Name:</span>{' '}
              <span className="font-mono">{blank(identification.exchangerNames, 35)}</span>
            </p>
            <p>
              <span className="font-medium">Dated:</span>{' '}
              <span className="font-mono">
                {signature?.capturedAt
                  ? new Date(signature.capturedAt).toLocaleDateString()
                  : new Date().toLocaleDateString()}
              </span>
            </p>
          </div>
          <div className="mt-2">
            <span className="text-xs font-medium">Signed:</span>{' '}
            {signature?.captured ? (
              <div className="inline-block p-2 ml-2 bg-background border-2 border-primary rounded min-h-[40px]">
                {signature.type === 'type' ? (
                  <span className="text-xl text-primary-900 dark:text-primary-100" style={{ fontFamily: 'cursive' }}>
                    {signature.value}
                  </span>
                ) : (
                  signature.value && (
                    <img src={signature.value} alt="Signature" className="max-h-[36px] object-contain inline-block align-middle" />
                  )
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground/80 italic ml-2">
                Signature will be captured below
              </span>
            )}
          </div>
        </div>

        <div>
          <p className="font-semibold text-sm mb-2">Received by Intermediary:</p>
          <p className="text-xs">
            OPEN1031, INC., a Delaware corporation, in its capacity as Qualified Intermediary.
          </p>
          {signature?.captured ? (
            <div className="mt-3">
              <QISignatureBlock signature={signature} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mt-2">
              <p>
                <span className="font-medium">By:</span>{' '}
                <span className="font-mono">{blank(undefined, 30)}</span>
              </p>
              <p>
                <span className="font-medium">Name:</span>{' '}
                <span className="font-mono">{blank(identification.intermediaryReceiverName, 30)}</span>
              </p>
              <p>
                <span className="font-medium">Title:</span>{' '}
                <span className="font-mono">{blank(identification.intermediaryReceiverTitle, 30)}</span>
              </p>
              <p>
                <span className="font-medium">Dated:</span>{' '}
                <span className="font-mono">{blank(undefined, 20)}</span>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
